import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import '../../css/forms/ApplicationForms.css';

const API_URL = 'https://rm-backend-service-505761517154.us-central1.run.app/submit-creator-application';

const CreatorApp = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    instagram_link: '',
    tiktok_link: '',
    major: '',
    graduation_date: '',
    skills: '',
    content_lifestyle: '',
    content_educational: '',
    content_entertainment: '',
    content_fashion: '',
    content_wellness: '',
    video_example_url: '',
    headshot_url: '',
    resume_url: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitAttempted && formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      'name', 'email', 'major', 'graduation_date', 'skills',
      'instagram_link', 'tiktok_link'
    ];

    requiredFields.forEach((field) => {
      if (!formData[field] || !formData[field].trim()) {
        errors[field] = 'This field is required';
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (formData.instagram_link && !/instagram\.com\/.+/i.test(formData.instagram_link)) {
      errors.instagram_link = 'Please enter a valid Instagram URL (e.g., instagram.com/username)';
    }

    if (formData.tiktok_link && !/tiktok\.com\/@.+/i.test(formData.tiktok_link)) {
      errors.tiktok_link = 'Please enter a valid TikTok URL (e.g., tiktok.com/@username)';
    }

    const urlFields = ['video_example_url', 'headshot_url', 'resume_url'];
    urlFields.forEach(field => {
      if (formData[field] && !/\..{2,}/.test(formData[field])) {
        errors[field] = 'Please enter a valid URL (e.g., drive.google.com/your-file)';
      }
    });

    const ratingFields = [
      'content_lifestyle', 'content_educational', 'content_entertainment',
      'content_fashion', 'content_wellness'
    ];
    ratingFields.forEach(field => {
      if (!formData[field]) {
        errors[field] = 'Please select a rating or N/A';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    window.scrollTo(0, 0); // Scroll to top immediately on submit

    if (!validateForm()) {
      const firstError = document.querySelector('.error');
      if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const contentType = response.headers.get("content-type");

      if (!response.ok) {
        if (contentType && contentType.includes("application/json")) {
          const errorJson = await response.json();
          throw new Error(errorJson.message || 'Failed to submit application');
        } else {
          const errorText = await response.text();
          throw new Error(`Server error: ${errorText.slice(0, 100)}...`);
        }
      }

      await response.json();
      setShowThankYouModal(true);
    } catch (error) {
      console.error('Submission error:', error.message);
      alert(`Error submitting form: ${error.message}`);
    }
  };

  const closeModal = () => {
    setShowThankYouModal(false);
    navigate('/roles');
    window.scrollTo(0, 0);
  };

  return (
    <div className="aam-container">
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      {showThankYouModal && (
        <div className="thank-you-modal">
          <div className="thank-you-content">
            <h2>Thank You for Applying!</h2>
            <p>We've received your application to join our creator network.</p>
            <p>Our team will review your information and get back to you if we have opportunities that match your skills and interests.</p>
            <p>In the meantime, keep creating amazing content!</p>
            <button onClick={closeModal}>Close</button>
          </div>
        </div>
      )}

      <div className="aam-header">
        <h1>Content Creator</h1>
        <div className="aam-meta">
          <span className="meta-item">For Aspiring Creators</span>
          <span className="meta-item">Flexible Opportunities</span>
        </div>
      </div>

      <div className="aam-content">
        <form onSubmit={handleSubmit} className="aam-form">
          <h2 className="form-title">Application Form</h2>

          {submitAttempted && Object.keys(formErrors).length > 0 && (
            <div className="form-error-message">
              Please correct the errors below before submitting
            </div>
          )}

          <div className="form-grid">
            {/* Basic Information */}
            <div className="form-group full-width">
              <label className="question-text">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={formErrors.name ? 'error' : ''}
              />
              {formErrors.name && <span className="error-message">{formErrors.name}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={formErrors.email ? 'error' : ''}
                placeholder="your@email.com"
              />
              {formErrors.email && <span className="error-message">{formErrors.email}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Instagram Link *</label>
              <input
                type="text"
                name="instagram_link"
                value={formData.instagram_link}
                onChange={handleChange}
                className={formErrors.instagram_link ? 'error' : ''}
                placeholder="instagram.com/username"
              />
              {formErrors.instagram_link && <span className="error-message">{formErrors.instagram_link}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">TikTok Link *</label>
              <input
                type="text"
                name="tiktok_link"
                value={formData.tiktok_link}
                onChange={handleChange}
                className={formErrors.tiktok_link ? 'error' : ''}
                placeholder="tiktok.com/@username"
              />
              {formErrors.tiktok_link && <span className="error-message">{formErrors.tiktok_link}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Major/Field of Study *</label>
              <input
                type="text"
                name="major"
                value={formData.major}
                onChange={handleChange}
                className={formErrors.major ? 'error' : ''}
                placeholder="e.g., Computer Science, Marketing"
              />
              {formErrors.major && <span className="error-message">{formErrors.major}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Expected Graduation Date *</label>
              <input
                type="text"
                name="graduation_date"
                value={formData.graduation_date}
                onChange={handleChange}
                className={formErrors.graduation_date ? 'error' : ''}
                placeholder="Month Year (e.g., May 2024)"
              />
              {formErrors.graduation_date && <span className="error-message">{formErrors.graduation_date}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">List your content creation skills *</label>
              <textarea
                name="skills"
                value={formData.skills}
                onChange={handleChange}
                className={formErrors.skills ? 'error' : ''}
                rows="3"
                placeholder="e.g., CapCut, Analyzing Trends, Canva, Videography, Storytelling, voiceovers, etc."
              />
              {formErrors.skills && <span className="error-message">{formErrors.skills}</span>}
            </div>

            {/* Content Preferences */}
            <div className="form-group full-width">
              <h3 className="rating-header">What type of content do you like creating the most?</h3>
              <p className="rating-subheader">
                Rate each category 1-5 (5 = Favorite); Select N/A if you don't make the given type of content.
              </p>

              {/* Rating Inputs */}
              {['content_lifestyle', 'content_educational', 'content_entertainment', 'content_fashion', 'content_wellness'].map(field => (
                <div key={field} className="rating-group">
                  <p className="rating-question">
                    {field === 'content_lifestyle' && 'Lifestyle (daily routines, vlogs, aesthetic content)'}
                    {field === 'content_educational' && 'Educational/Informative (tutorials, tips, how-to\'s)'}
                    {field === 'content_entertainment' && 'Entertainment (funny videos, challenges, trends)'}
                    {field === 'content_fashion' && 'Fashion & Beauty (outfits, makeup, skincare)'}
                    {field === 'content_wellness' && 'Self Improvement/Wellness (motivation, fitness, mental health)'}
                  </p>
                  <select
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    className={formErrors[field] ? 'error' : ''}
                  >
                    <option value="">Select rating</option>
                    <option value="0">N/A</option>
                    <option value="1">1 - Least favorite</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5 - Favorite</option>
                  </select>
                  {formErrors[field] && <span className="error-message">{formErrors[field]}</span>}
                </div>
              ))}
            </div>

            {/* Optional Uploads */}
            <div className="form-group full-width">
              <label className="question-text">Video Example URL (optional but recommended!)</label>
              <p className="app-subtext">Share a URL to a sample of your best work (YouTube, Google Drive, etc.)</p>
              <input
                type="text"
                name="video_example_url"
                value={formData.video_example_url}
                onChange={handleChange}
                className={formErrors.video_example_url ? 'error' : ''}
                placeholder="youtube.com/your-video or drive.google.com/your-file"
              />
              {formErrors.video_example_url && <span className="error-message">{formErrors.video_example_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Headshot URL (optional)</label>
              <p className="app-subtext">Provide a URL to your headshot (Google Drive, Imgur, etc.)</p>
              <input
                type="text"
                name="headshot_url"
                value={formData.headshot_url}
                onChange={handleChange}
                className={formErrors.headshot_url ? 'error' : ''}
                placeholder="drive.google.com/your-headshot"
              />
              {formErrors.headshot_url && <span className="error-message">{formErrors.headshot_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Resume URL (optional)</label>
              <p className="app-subtext">Include a URL to your resume (Google Drive, Dropbox, etc.)</p>
              <input
                type="text"
                name="resume_url"
                value={formData.resume_url}
                onChange={handleChange}
                className={formErrors.resume_url ? 'error' : ''}
                placeholder="drive.google.com/your-resume"
              />
              {formErrors.resume_url && <span className="error-message">{formErrors.resume_url}</span>}
            </div>
          </div>

          <button type="submit" className="submit-btn">Submit Application</button>
        </form>
      </div>
    </div>
  );
};

export default CreatorApp;